import { useCallback, useEffect, useRef, useState } from "react";
import Menu from "../../components/Menu"
import CalendarBlack from '../../assets/img/calendar-black.svg'
import { Button, Col, Form, Row, Card, Spinner, Modal, Badge } from "react-bootstrap";
import { Calendar, Clock, Eraser, Filter, User } from "lucide-react";
import api from "../../api";
import moment from "moment";
import CreditCard from '../../assets/img/credit-card-selected.png';
import PIX from '../../assets/img/pix-selected.png';
import Swal from "sweetalert2";
import { cpf } from "../../utils/formats";

const initialFilters = {
  startDate: moment().format("YYYY-MM-DD"),
  endDate: moment().format("YYYY-MM-DD"),
  shopper: '',
  customer: '',
  professional: '',
  agreement: ''
};

export default function AttendancesList() {
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);

  const [shoppers, setShoppers] = useState(null);
  const [professionals, setProfessionals] = useState(null);
  const [agreements, setAgreements] = useState(null);

  const [attendances, setAttendances] = useState([]);

  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [modalProcedures, setModalProcedures] = useState("");

  const getShoppers = async () => {
    await api.get('api/shoppers')
      .then((response) => {
        setShoppers(response.data.data);
      })
      .catch((error) => {
        console.error("Erro ao buscar shoppers:", error);
      });
  }

  const getAgreements = async () => {
    await api.get('api/agreements')
      .then((response) => {
        setAgreements(response.data.data);
      })
      .catch((error) => {
        console.error("Erro ao buscar convênios:", error);
      });
  }

  const getProfessionals = async () => {
    await api.get('api/professionals')
      .then((response) => {
        setProfessionals(response.data.data);
      })
      .catch((error) => {
        console.error("Erro ao buscar convênios:", error);
      });
  }

  const fetchAttendances = async (currentPage = 1, isAppend = false, currentFilters = filters) => {
    if (isAppend) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await api.post(`api/attendances/list/all?page=${currentPage}`, {
        date_start: currentFilters.startDate,
        date_end: currentFilters.endDate,
        shopper_id: currentFilters.shopper,
        customer_name: currentFilters.customer,
        professional_id: currentFilters.professional,
        agreement_name: currentFilters.agreement,
      });

      const { data: newItems, current_page, last_page, total } = response.data;

      // Se for append (scroll), junta com os antigos. Senão (novo filtro), substitui.
      if (isAppend) {
        setAttendances(prev => [...prev, ...newItems]);
      } else {
        setAttendances(newItems);
      }

      setPage(current_page);
      setHasMore(current_page < last_page);
      setTotalItems(total);

    } catch (error) {
      Swal.fire({
        title: "Erro ao buscar atendimentos",
        text: error.message || "Tente novamente mais tarde.",
        icon: "error",
        confirmButtonColor: "#3c8dbc",
      });
      console.error("Erro ao buscar atendimentos:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    getShoppers();
    getProfessionals();
    getAgreements();
    fetchAttendances(1, false, initialFilters);
  }, []);

  const observer = useRef();

  const lastElementRef = useCallback((node) => {
    if (loading || loadingMore) return; // Não faz nada se já estiver carregando

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      // Se o último elemento estiver visível e ainda houver páginas, busca a próxima
      if (entries[0].isIntersecting && hasMore) {
        fetchAttendances(page + 1, true, filters);
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore, page, filters]);

  // Função genérica para lidar com a mudança de qualquer input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleFilterSubmit = (e) => {
    e?.preventDefault();
    setPage(1); // Reseta a paginação ao fazer um novo filtro
    fetchAttendances(1, false, filters);
  };

  const handleClear = () => {
    setFilters(initialFilters);
    setPage(1);
    fetchAttendances(1, false, initialFilters);
  };

  const handleOpenModal = (procedures) => {
    setModalProcedures(procedures);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const getAttendanceTypeInfo = (type) => {
    switch (type) {
      case '0': return { label: 'Consulta', color: 'primary' };
      case '1': return { label: 'Retorno', color: 'info' };
      case '2': return { label: 'Exame', color: 'secondary' };
      default: return { label: 'Outro', color: 'light' };
    }
  };

  const screenTela = (
    <div className='screenContainer'>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="col-6 col-sm-6 col-lg-8 name-screen mb-0">
          <img style={{ width: '50px' }} className="me-3" alt="svg" src={CalendarBlack} />
          <span className="name-screen-title">Agendamentos</span>
        </h1>
        {totalItems > 0 && (
          <span className="text-muted fw-bold">Total: {totalItems} registros</span>
        )}
      </div>

      <div className="mb-4 p-3 border rounded bg-light">
        <Form onSubmit={handleFilterSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={4} controlId="filterInitialDate">
              <Form.Label>Data Inicial</Form.Label>
              <Form.Control type="date" name="startDate" value={filters.startDate} onChange={handleChange} />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={4} controlId="filterFinalDate">
              <Form.Label>Data Final</Form.Label>
              <Form.Control type="date" name="endDate" value={filters.endDate} onChange={handleChange} />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={4} controlId="filterShopper">
              <Form.Label>Empresa</Form.Label>
              <Form.Select name="shopper" value={filters.shopper} onChange={handleChange}>
                <option value="">TODAS</option>
                {shoppers?.map((shopper) => (
                  <option key={shopper.id} value={shopper.id}>{shopper.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={4} controlId="filterCustomer">
              <Form.Label>Paciente</Form.Label>
              <Form.Control type="text" name="customer" placeholder="Buscar por nome ou CPF" value={filters.customer} onChange={handleChange} />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={4} controlId="filterProfessional">
              <Form.Label>Profissional</Form.Label>
              <Form.Select name="professional" value={filters.professional} onChange={handleChange}>
                <option value="">TODOS</option>
                {professionals?.map((professional) => (
                  <option key={professional.id} value={professional.id}>{professional.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group as={Col} xs={12} md={4} controlId="filterAgreement">
              <Form.Label>Convênio</Form.Label>
              <Form.Select name="agreement" value={filters.agreement} onChange={handleChange}>
                <option value="">TODOS</option>
                {agreements?.map((agreement) => (
                  <option key={agreement.id} value={agreement.name}>{agreement.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <div className="d-flex justify-content-end align-items-center gap-3">
            <Button variant="outline-secondary" onClick={handleClear}>
              <Eraser size={20} className="me-2" /> Limpar
            </Button>
            <Button variant="primary" type="submit">
              <Filter size={20} className="me-2" /> Filtrar
            </Button>
          </div>
        </Form>
      </div>

      {/* Carregamento Inicial (Substitui a lista inteira) */}
      {loading ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-5">
          <Spinner animation="border" variant="primary" role="status" />
          <span className="mt-3 text-muted">Buscando agendamentos...</span>
        </div>
      ) : attendances.length > 0 ? (
        <>
          <Row md={2} className="g-4 mb-4">
            {attendances.map((item, index) => {
              const typeInfo = getAttendanceTypeInfo(item.type);
              
              // Verifica se é o último item da lista para atrelar a referência do Observer
              const isLastItem = attendances.length === index + 1;

              return (
                <Col key={`${item.id}-${index}`} ref={isLastItem ? lastElementRef : null}>
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center border-bottom">
                      <span className="fw-bold d-flex align-items-center gap-3">
                        <Calendar size={20} />
                        {moment(item.date_scheduling).format("DD/MM/YYYY")}
                      </span>
                      <span className="fw-bold d-flex align-items-center gap-3">
                        <Clock size={20} />
                        {item.time_starting_booked}
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        {item.authorization_code && <img src={CreditCard} alt="Cartão" title="Pagamento via Cartão" width={32} />}
                        {item.pix_status && <img src={PIX} alt="PIX" title="Pagamento via PIX" width={32} />}
                      </div>
                    </Card.Header>

                    <Card.Body>
                      <div className="mb-3">
                        <Badge pill bg={typeInfo.color} className="px-3 py-2">{typeInfo.label}</Badge>
                      </div>

                      <Card.Title className="fs-6 fw-bold text-truncate" title={item.customer_name}>
                        <User size={20} className="me-2" /> {item.customer_name}
                      </Card.Title>

                      <Card.Subtitle className="mt-2 mb-3 text-muted" style={{ fontSize: '0.85rem' }}>
                        CPF: {cpf(item.customer_cpf) || 'Não informado'}
                      </Card.Subtitle>

                      <div style={{ fontSize: '0.9rem' }}>
                        <p className="mb-1 text-truncate" title={item.professional_name}>
                          <strong>Profissional:</strong> {item.professional_name}
                        </p>
                        <p className="mb-1 text-truncate" title={item.shopper_name}>
                          <strong>Empresa:</strong> {item.shopper_name}
                        </p>
                        <p className="mb-1 text-truncate" title={item.agreement_name}>
                          <strong>Convênio:</strong> {item.agreement_name}
                        </p>
                        {(item.type === '0' || item.type === '1') && (
                          <p className="mb-1 text-truncate" title={item.procedure_name}>
                            <strong>Procedimento:</strong> {item.procedure_name}
                          </p>
                        )}
                      </div>
                    </Card.Body>

                    {(item.type !== '0' && item.type !== '1') && (
                      <Card.Footer className="bg-white border-top-0 pt-0">
                        <Button variant="outline-primary" size="sm" className="w-100" onClick={() => handleOpenModal(item.procedure_name)}>
                          Ver Procedimentos
                        </Button>
                      </Card.Footer>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
          
          {/* Carregamento da Próxima Página (Aparece no final do scroll) */}
          {loadingMore && (
            <div className="d-flex justify-content-center py-3">
              <Spinner animation="border" variant="secondary" size="sm" />
              <span className="ms-2 text-muted">Carregando mais...</span>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-5 text-muted">
          <h5>Nenhum agendamento encontrado para os filtros selecionados.</h5>
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Procedimentos do Agendamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 text-break">{modalProcedures || "Nenhum procedimento registrado."}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Fechar</Button>
        </Modal.Footer>
      </Modal>

    </div>
  );

  return (
    <Menu screen={screenTela} />
  )
}